import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";
import { CategoryChip } from "@/components/GSTR2TaxCodes";

const BY = { vendor: "Vendor", month: "Month", tax_code: "Tax code", hsn: "HSN code" };
const NOUN = { vendor: "vendors", month: "months", tax_code: "tax codes", hsn: "HSN codes" };

const Label = ({ by, r }) => {
  if (by === "tax_code") return <><span className="font-mono font-semibold">{r.key}</span>{r.label !== r.key ? <span className="ml-1 text-slate-600">{r.label}</span> : null}</>;
  if (by === "hsn") return <><span className="font-mono font-semibold">{r.key}</span>{r.description ? <span className="block text-[10px] text-slate-500 truncate" title={r.description}>{r.description}</span> : null}</>;
  return <>{r.label}{by === "vendor" && r.key !== r.label ? <span className="ml-1 text-[10px] font-mono text-slate-400">{r.key}</span> : null}</>;
};

/** Vendor / month / tax-code / HSN roll-up of the register (current vendor + date filters). Click a row → its lines. */
export function GSTR2Rollup({ params, onDrill }) {
  const [by, setBy] = useState("vendor");
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    const ctrl = new AbortController();
    axios.get(`${API}/reports/gstr2/summary`, { params: { ...params, by }, signal: ctrl.signal }).then((r) => setD(r.data)).catch(() => {});
    return () => ctrl.abort();
  }, [params, by]);
  const rows = (d?.rows || []).filter((r) => !q || `${r.label} ${r.key} ${r.gstin || ""} ${r.description || ""}`.toLowerCase().includes(q.toLowerCase()));
  const second = by === "vendor" ? "GSTIN" : by === "tax_code" ? "Category" : by === "hsn" ? "Rate" : "";
  const heads = [BY[by], second, by === "hsn" ? "Materials" : "", by === "hsn" ? "Qty" : "", "Lines", "Taxable", "IGST", "CGST", "SGST", "Total tax", "Eligible", "Ineligible", "RCM"].filter(Boolean);
  return (
    <Card className="border-slate-200 shadow-sm fade-in-up" data-testid="gstr2-rollup">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Roll-up</p>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
              {[["vendor", "By vendor"], ["month", "By month"], ["tax_code", "By tax code"], ["hsn", "By HSN code"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setBy(k)} data-testid={`gstr2-rollup-${k}`} className={`px-3 py-1.5 ${by === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
            {d && <span className="text-xs text-slate-500">{d.count} {NOUN[by]} · click a row for its lines</span>}
          </div>
          {(by === "vendor" || by === "hsn") && <Input data-testid="gstr2-rollup-search" placeholder={by === "hsn" ? "Search HSN / material…" : "Search vendor / GSTIN…"} value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-56 text-xs" />}
        </div>
        {by === "hsn" && <p className="text-[11px] text-slate-500 mb-2" data-testid="gstr2-hsn-note">HSN from the product master (tax control code) of the materials on each supplier invoice; taxable = material lines of the SAP document, tax apportioned by taxable share. Service / expense lines without a material are grouped under "{d?.no_hsn_key || "(no HSN)"}". Re-sync SAP data if this list is empty.</p>}
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {heads.map((h) => (
                  <TableHead key={h} className={`text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap ${["Vendor", "Month", "Tax code", "HSN code", "GSTIN", "Category"].includes(h) ? "" : "text-right"}`}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key} className="border-b border-slate-100 hover:bg-sky-50/60 cursor-pointer" onClick={() => onDrill({ metric: by, value: r.key, title: `${BY[by]}: ${r.label}` })} data-testid={`gstr2-rollup-row-${r.key}`}>
                  <TableCell className="text-xs text-slate-800 whitespace-nowrap max-w-[260px] truncate" title={r.label}><Label by={by} r={r} /></TableCell>
                  {by === "vendor" && <TableCell className="text-[11px] font-mono text-slate-500">{r.gstin || "—"}</TableCell>}
                  {by === "tax_code" && <TableCell><CategoryChip cat={r.category} /></TableCell>}
                  {by === "hsn" && <TableCell className="text-xs text-right text-slate-600">{r.rate != null ? `${r.rate}%` : "—"}</TableCell>}
                  {by === "hsn" && <TableCell className="text-xs text-right text-slate-600">{r.materials || "—"}</TableCell>}
                  {by === "hsn" && <TableCell className="text-xs text-right text-slate-600 tabular-nums">{r.qty ? r.qty.toLocaleString("en-IN") : "—"}</TableCell>}
                  <TableCell className="text-xs text-right text-slate-600">{r.count}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{inr(r.taxable_value)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{r.igst ? inr(r.igst) : "—"}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{r.cgst ? inr(r.cgst) : "—"}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{r.sgst ? inr(r.sgst) : "—"}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums font-semibold">{inr(r.total_tax)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums text-emerald-700">{inrShort(r.eligible_tax)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums text-red-700">{r.ineligible_tax ? inrShort(r.ineligible_tax) : "—"}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums text-sky-700">{r.rcm_tax ? inrShort(r.rcm_tax) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
